import { ZoomIn, ZoomOut } from "lucide-mui";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Slider from "@mui/material/Slider";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { type KeyboardEvent, type PointerEvent, type WheelEvent, useEffect, useRef, useState } from "react";

const VIEW = 320; // the crop window, in CSS px
const MAX_ZOOM = 4;
const NUDGE = 10;

interface Offset {
  x: number;
  y: number;
}

interface ImageCropDialogProps {
  /** The picked file; the dialog is open while there is one. */
  file: File | null;
  shape?: "circle" | "square";
  /** Width and height of the saved image, in px. */
  outputSize?: number;
  title?: string;
  pending?: boolean;
  onCancel: () => void;
  onCrop: (cropped: File) => void;
}

function useImage(file: File | null) {
  const [image, setImage] = useState<HTMLImageElement | null>(null);
  useEffect(() => {
    setImage(null);
    if (!file) return;
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => setImage(img);
    img.src = url;
    return () => URL.revokeObjectURL(url);
  }, [file]);
  return image;
}

function toBlob(canvas: HTMLCanvasElement, type: string): Promise<Blob | null> {
  return new Promise((resolve) => canvas.toBlob(resolve, type, 0.9));
}

/**
 * Square crop of a picked image before it's uploaded: drag to move, scroll or
 * the slider to zoom, arrow keys and +/- from the keyboard. Saves a
 * `outputSize`² WebP (PNG where the browser can't encode WebP).
 */
export default function ImageCropDialog({
  file,
  shape = "circle",
  outputSize = 512,
  title = "Crop photo",
  pending = false,
  onCancel,
  onCrop,
}: ImageCropDialogProps) {
  const image = useImage(file);
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState<Offset>({ x: 0, y: 0 });
  const drag = useRef<{ start: Offset; pointer: Offset } | null>(null);

  useEffect(() => {
    setZoom(1);
    setOffset({ x: 0, y: 0 });
  }, [image]);

  // Displayed px per image px: the image covers the window at zoom 1.
  const base = image ? Math.max(VIEW / image.naturalWidth, VIEW / image.naturalHeight) : 1;
  const scale = base * zoom;
  const width = image ? image.naturalWidth * scale : VIEW;
  const height = image ? image.naturalHeight * scale : VIEW;

  // Keep the window covered: the image never slides past an edge.
  const clamp = (o: Offset, w = width, h = height): Offset => ({
    x: Math.max(-(w - VIEW) / 2, Math.min((w - VIEW) / 2, o.x)),
    y: Math.max(-(h - VIEW) / 2, Math.min((h - VIEW) / 2, o.y)),
  });

  const zoomTo = (next: number) => {
    const z = Math.max(1, Math.min(MAX_ZOOM, next));
    if (!image) return;
    const w = image.naturalWidth * base * z;
    const h = image.naturalHeight * base * z;
    // Zoom about the window's centre.
    setOffset((o) => clamp({ x: (o.x * z) / zoom, y: (o.y * z) / zoom }, w, h));
    setZoom(z);
  };

  const onPointerDown = (e: PointerEvent<HTMLDivElement>) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    drag.current = { start: offset, pointer: { x: e.clientX, y: e.clientY } };
  };
  const onPointerMove = (e: PointerEvent<HTMLDivElement>) => {
    const d = drag.current;
    if (!d) return;
    setOffset(clamp({ x: d.start.x + e.clientX - d.pointer.x, y: d.start.y + e.clientY - d.pointer.y }));
  };
  const onPointerUp = () => {
    drag.current = null;
  };
  const onWheel = (e: WheelEvent<HTMLDivElement>) => zoomTo(zoom * (e.deltaY < 0 ? 1.08 : 1 / 1.08));
  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const moves: Record<string, Offset> = {
      ArrowLeft: { x: NUDGE, y: 0 },
      ArrowRight: { x: -NUDGE, y: 0 },
      ArrowUp: { x: 0, y: NUDGE },
      ArrowDown: { x: 0, y: -NUDGE },
    };
    if (moves[e.key]) {
      e.preventDefault();
      setOffset((o) => clamp({ x: o.x + moves[e.key].x, y: o.y + moves[e.key].y }));
    } else if (e.key === "+" || e.key === "=") {
      zoomTo(zoom + 0.2);
    } else if (e.key === "-") {
      zoomTo(zoom - 0.2);
    }
  };

  const save = async () => {
    if (!image || !file) return;
    const canvas = document.createElement("canvas");
    canvas.width = outputSize;
    canvas.height = outputSize;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.imageSmoothingQuality = "high";
    // The window's top-left corner, in image px.
    const sx = (width / 2 - VIEW / 2 - offset.x) / scale;
    const sy = (height / 2 - VIEW / 2 - offset.y) / scale;
    ctx.drawImage(image, sx, sy, VIEW / scale, VIEW / scale, 0, 0, outputSize, outputSize);
    let blob = await toBlob(canvas, "image/webp");
    if (!blob || blob.type !== "image/webp") blob = await toBlob(canvas, "image/png");
    if (!blob) return;
    const ext = blob.type === "image/webp" ? "webp" : "png";
    const name = `${file.name.replace(/\.[^.]+$/, "") || "photo"}.${ext}`;
    onCrop(new File([blob], name, { type: blob.type }));
  };

  return (
    <Dialog open={!!file} onClose={pending ? undefined : onCancel} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ alignItems: "center", pt: 0.5 }}>
          <Box
            role="img"
            aria-label="Crop area. Drag or use the arrow keys to move, scroll or plus and minus to zoom."
            tabIndex={0}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
            onWheel={onWheel}
            onKeyDown={onKeyDown}
            sx={{
              position: "relative",
              width: VIEW,
              height: VIEW,
              overflow: "hidden",
              borderRadius: 1,
              bgcolor: "action.hover",
              cursor: "grab",
              "&:active": { cursor: "grabbing" },
              touchAction: "none",
              userSelect: "none",
              outline: "none",
              "&:focus-visible": { outline: "2px solid", outlineColor: "primary.main", outlineOffset: 2 },
            }}
          >
            {image && (
              <Box
                component="img"
                src={image.src}
                alt=""
                draggable={false}
                sx={{
                  position: "absolute",
                  left: "50%",
                  top: "50%",
                  width,
                  height,
                  maxWidth: "none",
                  transform: `translate(calc(-50% + ${offset.x}px), calc(-50% + ${offset.y}px))`,
                  pointerEvents: "none",
                }}
              />
            )}
            {/* Dims what falls outside the saved image. */}
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                pointerEvents: "none",
                borderRadius: shape === "circle" ? "50%" : 1,
                boxShadow: "0 0 0 9999px rgba(var(--mui-palette-background-defaultChannel) / 0.7)",
                border: 1,
                borderColor: "divider",
              }}
            />
          </Box>
          <Stack direction="row" spacing={1.5} sx={{ alignItems: "center", width: VIEW }}>
            <ZoomOut sx={{ color: "text.secondary", fontSize: 18 }} />
            <Slider
              size="small"
              min={1}
              max={MAX_ZOOM}
              step={0.01}
              value={zoom}
              onChange={(_, v) => zoomTo(v as number)}
              aria-label="Zoom"
              disabled={!image}
            />
            <ZoomIn sx={{ color: "text.secondary", fontSize: 18 }} />
          </Stack>
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            Drag to move, scroll to zoom
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={pending}>
          Cancel
        </Button>
        <Button variant="contained" onClick={save} loading={pending} disabled={!image}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
